import { create } from 'zustand';
import { WEB_HOME_PANEL, isWebApp } from '../platform/target';

/**
 * Pure UI / modal-visibility state lifted out of App.js.
 *
 * State is read reactively via the `useUiModalStore()` hook. Setters are also
 * exported as standalone module-level functions (named identically to the old
 * `useState` setters, e.g. `setShowCollectionModal`) so App.js call sites keep
 * working unchanged. Because they are module-scope imports, React's
 * exhaustive-deps lint correctly treats them as stable and does not flag them.
 * Setters mirror React `useState` semantics: value or updater function.
 *
 * Excludes update-* (see updateStore), activeTabType (tabsStore), and
 * canPublishToNetworkPlan (workspaceStore) which belong to other domains.
 */
const initialState = {
  // main nav
  // 'collections' | 'history' | 'teams' | '<extension>.panel'. The web app has no
  // API client, so it opens on Boards.
  activeTab: isWebApp() ? WEB_HOME_PANEL : 'collections',

  // workspace-related modals
  forkModalWorkspace: null,
  gitConnectWorkspace: null,
  gitConflict: null, // { workspace, conflicts }
  gitSecrets: null, // { workspace, findings, commitAnyway } — git sync card 5
  publishModalWorkspace: null,
  publishNetworkWorkspace: null,
  showWorkspaceModal: false,
  workspaceModalMode: 'create',
  selectedWorkspaceForEdit: null,

  // teams modal
  showTeamsModal: false,
  teamsModalMode: 'create',
  selectedTeam: null,

  // command palette
  showCommandPalette: false,
  commandPaletteTeamDetailId: null,
  // The scope chip the palette opens with: a provider id ('documents' when
  // opened from the Documents panel, card 1.3 / PLAN D2), or null.
  commandPaletteFilter: null,
  // 'quickOpen' when opened with ⌘P / Ctrl+P (recent items and tabs first), or null.
  commandPaletteMode: null,

  // auth / settings / billing
  authModalOpen: false,
  authModalMode: 'login',
  settingsModalOpen: false,
  settingsInitialTab: null,
  upgradeModalOpen: false,
  upgradeReason: '',
  planModalOpen: false,

  // deep-link driven
  shareItem: null,
  // Deep links set a bare token string; the chat share card sets
  // `{ token, channelId, enc }` — an encrypted snapshot can only be opened with
  // the key of the channel it was sent in, and a deep link has no channel.
  // `ModalStack` normalises both shapes.
  shareAcceptToken: null,
  // "Share → In Chat" recipient picker: { type: 'workspace'|'collection'|'request', item, parentCollection? }
  shareToChatTarget: null,
  // Play on a shared-request chat card: { token, label, channelId, enc } — an App-level effect
  // fetches the share data and opens it as an unsaved request tab.
  sharedRequestOpen: null,
  networkImportSlug: null, // slug from apiboo://network/
  pendingNetworkSlug: null, // slug waiting for login
  joinDeepLinkCode: null, // invite code from apiboo://join/
  resetDeepLink: null, // { token, email } from apiboo://reset-password
  pendingTeamInviteToken: null,

  // collection modals
  showCollectionModal: false,
  showImportCollectionModal: false,
  importCollectionLoading: false,
  showExportCollectionModal: false,
  exportCollectionTarget: null,
  showCollectionRunnerModal: false,
  collectionRunnerTarget: null,
  collectionRunnerAutoRun: false,
  collectionRunnerFolderId: null,
  showCodeGenModal: false,
  codeGenTarget: null,
  showClearHistoryModal: false,
  showCollectionSelectionModal: false,
  showExportCollectionPickerModal: false,
  pendingRequestData: null,
  pendingRequestTabId: null,

  // misc panels/modals
  showEnvironmentsModal: false,
  aiPanelOpen: false,
};

function setterName(key) {
  return `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

function buildSetters(set) {
  const setters = {};
  for (const key of Object.keys(initialState)) {
    setters[setterName(key)] = (value) =>
      set((state) => ({
        [key]: typeof value === 'function' ? value(state[key]) : value,
      }));
  }
  return setters;
}

export const useUiModalStore = create((set) => ({
  ...initialState,
  ...buildSetters(set),
  reset: () => set({ ...initialState }),
}));

// Stable module-level setter functions (delegate to the store actions).
// Importing these keeps App.js call sites unchanged and lint-clean.
export const setActiveTab = (v) => useUiModalStore.getState().setActiveTab(v);
export const setForkModalWorkspace = (v) => useUiModalStore.getState().setForkModalWorkspace(v);
export const setGitConnectWorkspace = (v) => useUiModalStore.getState().setGitConnectWorkspace(v);
export const setGitConflict = (v) => useUiModalStore.getState().setGitConflict(v);
export const setGitSecrets = (v) => useUiModalStore.getState().setGitSecrets(v);
export const setPublishModalWorkspace = (v) => useUiModalStore.getState().setPublishModalWorkspace(v);
export const setPublishNetworkWorkspace = (v) => useUiModalStore.getState().setPublishNetworkWorkspace(v);
export const setShowWorkspaceModal = (v) => useUiModalStore.getState().setShowWorkspaceModal(v);
export const setWorkspaceModalMode = (v) => useUiModalStore.getState().setWorkspaceModalMode(v);
export const setSelectedWorkspaceForEdit = (v) => useUiModalStore.getState().setSelectedWorkspaceForEdit(v);
export const setShowTeamsModal = (v) => useUiModalStore.getState().setShowTeamsModal(v);
export const setTeamsModalMode = (v) => useUiModalStore.getState().setTeamsModalMode(v);
export const setSelectedTeam = (v) => useUiModalStore.getState().setSelectedTeam(v);
export const setShowCommandPalette = (v) => useUiModalStore.getState().setShowCommandPalette(v);
export const setCommandPaletteFilter = (v) => useUiModalStore.getState().setCommandPaletteFilter(v);
export const setCommandPaletteMode = (v) => useUiModalStore.getState().setCommandPaletteMode(v);
export const setCommandPaletteTeamDetailId = (v) => useUiModalStore.getState().setCommandPaletteTeamDetailId(v);
export const setAuthModalOpen = (v) => useUiModalStore.getState().setAuthModalOpen(v);
export const setAuthModalMode = (v) => useUiModalStore.getState().setAuthModalMode(v);
export const setSettingsModalOpen = (v) => useUiModalStore.getState().setSettingsModalOpen(v);
export const setSettingsInitialTab = (v) => useUiModalStore.getState().setSettingsInitialTab(v);
export const setUpgradeModalOpen = (v) => useUiModalStore.getState().setUpgradeModalOpen(v);
export const setUpgradeReason = (v) => useUiModalStore.getState().setUpgradeReason(v);
export const setPlanModalOpen = (v) => useUiModalStore.getState().setPlanModalOpen(v);
export const setShareItem = (v) => useUiModalStore.getState().setShareItem(v);
export const setShareAcceptToken = (v) => useUiModalStore.getState().setShareAcceptToken(v);
export const setShareToChatTarget = (v) => useUiModalStore.getState().setShareToChatTarget(v);
export const setSharedRequestOpen = (v) => useUiModalStore.getState().setSharedRequestOpen(v);
export const setNetworkImportSlug = (v) => useUiModalStore.getState().setNetworkImportSlug(v);
export const setPendingNetworkSlug = (v) => useUiModalStore.getState().setPendingNetworkSlug(v);
export const setJoinDeepLinkCode = (v) => useUiModalStore.getState().setJoinDeepLinkCode(v);
export const setResetDeepLink = (v) => useUiModalStore.getState().setResetDeepLink(v);
export const setPendingTeamInviteToken = (v) => useUiModalStore.getState().setPendingTeamInviteToken(v);
export const setShowCollectionModal = (v) => useUiModalStore.getState().setShowCollectionModal(v);
export const setShowImportCollectionModal = (v) => useUiModalStore.getState().setShowImportCollectionModal(v);
export const setImportCollectionLoading = (v) => useUiModalStore.getState().setImportCollectionLoading(v);
export const setShowExportCollectionModal = (v) => useUiModalStore.getState().setShowExportCollectionModal(v);
export const setExportCollectionTarget = (v) => useUiModalStore.getState().setExportCollectionTarget(v);
export const setShowCollectionRunnerModal = (v) => useUiModalStore.getState().setShowCollectionRunnerModal(v);
export const setCollectionRunnerTarget = (v) => useUiModalStore.getState().setCollectionRunnerTarget(v);
export const setCollectionRunnerAutoRun = (v) => useUiModalStore.getState().setCollectionRunnerAutoRun(v);
export const setCollectionRunnerFolderId = (v) => useUiModalStore.getState().setCollectionRunnerFolderId(v);
export const setShowCodeGenModal = (v) => useUiModalStore.getState().setShowCodeGenModal(v);
export const setCodeGenTarget = (v) => useUiModalStore.getState().setCodeGenTarget(v);
export const setShowClearHistoryModal = (v) => useUiModalStore.getState().setShowClearHistoryModal(v);
export const setShowCollectionSelectionModal = (v) => useUiModalStore.getState().setShowCollectionSelectionModal(v);
export const setShowExportCollectionPickerModal = (v) => useUiModalStore.getState().setShowExportCollectionPickerModal(v);
export const setPendingRequestData = (v) => useUiModalStore.getState().setPendingRequestData(v);
export const setPendingRequestTabId = (v) => useUiModalStore.getState().setPendingRequestTabId(v);
export const setShowEnvironmentsModal = (v) => useUiModalStore.getState().setShowEnvironmentsModal(v);
export const setAiPanelOpen = (v) => useUiModalStore.getState().setAiPanelOpen(v);
